import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { Message } from '@/types'
import { TypingIndicator } from './TypingIndicator'
import { useChatStore } from '@/store/chatStore'
import { useWhatsAppHandoff } from '@/hooks/useWhatsAppHandoff'

// ─────────────────────────────────────────────
// AssistantMessage
// Left-aligned bubble for AI responses.
// Renders markdown (tables, bold, code blocks).
// Shows TypingIndicator while streaming.
// Shows RAG source chips when response is done.
// ─────────────────────────────────────────────

interface Props {
  message: Message
}

export function AssistantMessage({ message }: Props) {
  const hasAppointmentSchedule = useChatStore((s) => s.hasAppointmentSchedule)
  const bookingStep            = useChatStore((s) => s.bookingStep)
  const startBooking           = useChatStore((s) => s.startBooking)
  const { canWhatsApp, openWhatsApp } = useWhatsAppHandoff()

  const time = message.timestamp.toLocaleTimeString([], {
    hour:   '2-digit',
    minute: '2-digit',
  })

  const isEmpty = !message.content && message.isStreaming

  // Restriction messages get a distinct rose/red style so end-users
  // immediately understand this is a system notice, not an AI answer
  const bubbleClass = message.isRestriction
    ? `
        px-4 py-2.5 rounded-2xl rounded-tl-sm
        text-sm leading-relaxed italic
        bg-rose-50 border border-rose-200
        text-rose-700
      `
    : `
        px-4 py-2.5 rounded-2xl rounded-tl-sm
        text-sm leading-relaxed
        bg-[var(--color-msg-ai)]
        text-[var(--color-msg-ai-text)]
      `

  return (
    <div className="flex justify-start mb-3">
      {/* Avatar */}
      <div className="
        w-7 h-7 rounded-full flex-shrink-0 mt-1 mr-2
        bg-[var(--color-brand)] flex items-center justify-center
      ">
        <span className="text-white text-xs font-bold">D</span>
      </div>

      <div className="max-w-[80%] flex flex-col gap-1">
        {/* Message bubble */}
        <div className={bubbleClass}>
          {isEmpty ? (
            <TypingIndicator />
          ) : message.isRestriction ? (
            // Restriction — plain text with warning icon, no markdown rendering
            <span>⚠️ {message.content}</span>
          ) : (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                code({ children, className }) {
                  const isBlock = className?.includes('language-')
                  return isBlock ? (
                    <pre className="
                      bg-[var(--color-surface)] rounded-lg p-3 my-2
                      overflow-x-auto text-xs
                      border border-[var(--color-border)]
                    ">
                      <code>{children}</code>
                    </pre>
                  ) : (
                    <code className="
                      bg-[var(--color-surface)] px-1.5 py-0.5
                      rounded text-xs font-mono
                    ">
                      {children}
                    </code>
                  )
                },
                table({ children }) {
                  return (
                    <div className="overflow-x-auto my-2">
                      <table className="
                        text-xs border-collapse w-full
                        border border-[var(--color-border)]
                      ">
                        {children}
                      </table>
                    </div>
                  )
                },
                th({ children }) {
                  return (
                    <th className="
                      border border-[var(--color-border)] px-3 py-1.5
                      bg-[var(--color-surface)] font-semibold text-left
                    ">
                      {children}
                    </th>
                  )
                },
                td({ children }) {
                  return (
                    <td className="
                      border border-[var(--color-border)] px-3 py-1.5
                    ">
                      {children}
                    </td>
                  )
                },
              }}
            >
              {message.content}
            </ReactMarkdown>
          )}
        </div>

        {/* RAG source chips — not shown on restriction messages */}
        {!message.isRestriction && !message.isStreaming && message.sources && message.sources.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {message.sources.map((source) => (
              <span
                key={source.chunkId}
                title={source.excerpt}
                className="
                  text-[11px] px-2 py-0.5 rounded-full
                  bg-[var(--color-surface)]
                  border border-[var(--color-border)]
                  text-[var(--color-text-sub)]
                  cursor-default
                "
              >
                📄 {source.documentTitle}
              </span>
            ))}
          </div>
        )}

        {/* Action buttons — shown after every completed non-restriction AI response.
            WhatsApp sits beside Book so a visitor whose question wasn't fully
            answered can reach a human without leaving the conversation. */}
        {!message.isStreaming && !message.isRestriction && bookingStep === null &&
         (hasAppointmentSchedule || canWhatsApp) && (
          <div className="mt-1 flex items-center gap-1.5 flex-wrap self-start">
            {hasAppointmentSchedule && (
              <button
                onClick={startBooking}
                className="
                  flex items-center gap-1.5
                  text-[11px] px-2.5 py-1.5 rounded-lg
                  border border-[var(--color-brand)]
                  text-[var(--color-brand)]
                  hover:bg-[var(--color-brand)] hover:text-white
                  transition-colors
                "
              >
                📅 Book an Appointment
              </button>
            )}

            {canWhatsApp && (
              <button
                onClick={openWhatsApp}
                className="
                  flex items-center gap-1.5
                  text-[11px] px-2.5 py-1.5 rounded-lg
                  border border-[#25d366] text-[#25d366]
                  hover:bg-[#25d366] hover:text-white
                  transition-colors
                "
              >
                💬 WhatsApp
              </button>
            )}
          </div>
        )}

        <span className="text-[11px] text-[var(--color-text-sub)] pl-1">
          {time}
        </span>
      </div>
    </div>
  )
}